import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatFileSize } from '../../utils/formatFileSize';

// Sorting helpers
const handleSort = (field, currentField, currentDirection, setField, setDirection) => {
  if (currentField === field) {
    setDirection(currentDirection === 'asc' ? 'desc' : 'asc');
  } else {
    setField(field);
    setDirection('asc');
  }
};

const getSortIcon = (field, currentField, currentDirection) => {
  if (currentField !== field) return null;
  return currentDirection === 'asc' ? ' ▲' : ' ▼';
};

const sortData = (data, field, direction) => {
  if (!field) return data;

  return [...data].sort((a, b) => {
    let aVal, bVal;

    if (field === 'storage_limit_bytes' || field === 'user_count' || field === 'display_order') {
      aVal = parseInt(a[field]) || 0;
      bVal = parseInt(b[field]) || 0;
    } else {
      aVal = a[field];
      bVal = b[field];
    }

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const stringA = String(aVal).toLowerCase();
    const stringB = String(bVal).toLowerCase();
    const comparison = stringA.localeCompare(stringB);
    return direction === 'asc' ? comparison : -comparison;
  });
};

// Storage unit options for the form
const STORAGE_UNITS = [
  { label: 'MB', multiplier: 1024 * 1024 },
  { label: 'GB', multiplier: 1024 * 1024 * 1024 },
  { label: 'TB', multiplier: 1024 * 1024 * 1024 * 1024 }
];

function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    name: '',
    storageValue: '',
    storageUnit: 'GB',
    description: '',
    displayOrder: 0,
    isDefault: false,
    isActive: true
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      alert('Failed to fetch plans: ' + (error.response?.data?.error || error.message));
    }
  };

  const bytesToUnit = (bytes) => {
    if (!bytes) return { value: '', unit: 'GB' };
    const tb = 1024 * 1024 * 1024 * 1024;
    const gb = 1024 * 1024 * 1024;
    const mb = 1024 * 1024;
    if (bytes >= tb && bytes % tb === 0) return { value: bytes / tb, unit: 'TB' };
    if (bytes >= gb && bytes % gb === 0) return { value: bytes / gb, unit: 'GB' };
    return { value: bytes / mb, unit: 'MB' };
  };

  const openAddModal = () => {
    setEditingPlan(null);
    setIsAdding(true);
    setFormData({
      name: '',
      storageValue: '',
      storageUnit: 'GB',
      description: '',
      displayOrder: 0,
      isDefault: false,
      isActive: true,
      priceMonthly: '',
      priceYearly: '',
      maxReflections: '',
      maxFamilyMembers: '',
      extraMemberMonthly: '',
      allowsExport: true,
      allowsSharingSend: true,
      allowsFamily: false,
      featuresText: '',
    });
  };

  const openEditModal = (plan) => {
    const { value, unit } = bytesToUnit(parseInt(plan.storage_limit_bytes));
    const features = Array.isArray(plan.features_json) ? plan.features_json.join('\n') : '';
    setEditingPlan(plan);
    setIsAdding(false);
    setFormData({
      name: plan.name,
      storageValue: value,
      storageUnit: unit,
      description: plan.description || '',
      displayOrder: plan.display_order || 0,
      isDefault: plan.is_default,
      isActive: plan.is_active,
      priceMonthly: plan.price_monthly_cents != null ? (plan.price_monthly_cents / 100).toFixed(2) : '',
      priceYearly: plan.price_yearly_cents != null ? (plan.price_yearly_cents / 100).toFixed(2) : '',
      maxReflections: plan.max_reflections || '',
      maxFamilyMembers: plan.max_family_members || '',
      extraMemberMonthly: plan.extra_member_monthly_cents != null ? (plan.extra_member_monthly_cents / 100).toFixed(2) : '',
      allowsExport: plan.allows_export !== false,
      allowsSharingSend: plan.allows_sharing_send !== false,
      allowsFamily: plan.allows_family === true,
      featuresText: features,
    });
  };

  const closeModal = () => {
    setEditingPlan(null);
    setIsAdding(false);
  };

  const getStorageLimitBytes = () => {
    const unit = STORAGE_UNITS.find(u => u.label === formData.storageUnit);
    return Math.round(parseFloat(formData.storageValue) * (unit?.multiplier || 1));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const storageLimitBytes = getStorageLimitBytes();
    if (!storageLimitBytes || storageLimitBytes <= 0) {
      alert('Storage limit must be a positive number');
      return;
    }

    try {
      const featuresJson = formData.featuresText
        ? formData.featuresText.split('\n').map(s => s.trim()).filter(Boolean)
        : null;
      const payload = {
        name: formData.name,
        storageLimitBytes,
        description: formData.description || null,
        displayOrder: parseInt(formData.displayOrder) || 0,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
        priceMonthly: formData.priceMonthly ? parseFloat(formData.priceMonthly) : null,
        priceYearly: formData.priceYearly ? parseFloat(formData.priceYearly) : null,
        maxReflections: formData.maxReflections ? parseInt(formData.maxReflections) : null,
        maxFamilyMembers: formData.maxFamilyMembers ? parseInt(formData.maxFamilyMembers) : null,
        extraMemberMonthly: formData.extraMemberMonthly ? parseFloat(formData.extraMemberMonthly) : null,
        allowsExport: formData.allowsExport,
        allowsSharingSend: formData.allowsSharingSend,
        allowsFamily: formData.allowsFamily,
        featuresJson,
      };

      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, payload);
      } else {
        await api.post('/admin/plans', payload);
      }

      fetchPlans();
      closeModal();
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Failed to save plan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (plan) => {
    if (parseInt(plan.user_count) > 0) {
      alert(`Cannot delete "${plan.name}": ${plan.user_count} user(s) are assigned to it. Reassign them first.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the "${plan.name}" plan?`)) {
      return;
    }
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete plan: ' + (error.response?.data?.error || error.message));
    }
  };

  const showModal = isAdding || editingPlan;

  return (
    <>
      <div className="admin-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Subscription Plans</h2>
          <button className="btn-save" onClick={openAddModal}>Add Plan</button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('name', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Name{getSortIcon('name', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('storage_limit_bytes', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Storage Limit{getSortIcon('storage_limit_bytes', sortField, sortDirection)}
              </th>
              <th>Description</th>
              <th
                onClick={() => handleSort('user_count', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Users{getSortIcon('user_count', sortField, sortDirection)}
              </th>
              <th>Default</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortData(plans, sortField, sortDirection).map(plan => (
              <tr key={plan.id}>
                <td>{plan.name}</td>
                <td>{formatFileSize(parseInt(plan.storage_limit_bytes))}</td>
                <td>{plan.description || ''}</td>
                <td>{plan.user_count || 0}</td>
                <td>{plan.is_default ? 'Yes' : ''}</td>
                <td>{plan.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button className="btn-edit" onClick={() => openEditModal(plan)}>Edit</button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(plan)}
                    disabled={parseInt(plan.user_count) > 0}
                    title={parseInt(plan.user_count) > 0 ? 'Reassign users before deleting' : ''}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPlan ? `Edit Plan: ${editingPlan.name}` : 'Add Plan'}</h3>
              <button onClick={closeModal}>&#10005;</button>
            </div>

            <form onSubmit={handleSave} className="admin-form compact-form">
              <div className="compact-row">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g. Starter, Plus, Premium"
                />
              </div>

              <div className="compact-row">
                <label>Storage Limit</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.storageValue}
                    onChange={(e) => setFormData({ ...formData, storageValue: e.target.value })}
                    required
                    min="1"
                    step="any"
                    style={{ flex: 1 }}
                  />
                  <select
                    value={formData.storageUnit}
                    onChange={(e) => setFormData({ ...formData, storageUnit: e.target.value })}
                    style={{ width: '80px' }}
                  >
                    {STORAGE_UNITS.map(u => (
                      <option key={u.label} value={u.label}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="compact-row">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>

              <div className="compact-row">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                  min="0"
                />
              </div>

              <div className="compact-toggle-grid">
                <label className="compact-check">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  <span>Default plan (assigned to new users)</span>
                </label>
                <label className="compact-check">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Active (can be assigned to users)</span>
                </label>
              </div>

              <hr style={{ margin: '0.75rem 0', opacity: 0.2 }} />
              <div style={{ fontSize: '0.8em', fontWeight: 'bold', opacity: 0.7, marginBottom: '0.5rem' }}>PRICING &amp; FEATURES</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div className="compact-row">
                  <label>Monthly price ($)</label>
                  <input type="number" step="0.01" min="0" placeholder="e.g. 6.00 (blank = free)"
                    value={formData.priceMonthly} onChange={e => setFormData({ ...formData, priceMonthly: e.target.value })} />
                </div>
                <div className="compact-row">
                  <label>Yearly price ($)</label>
                  <input type="number" step="0.01" min="0" placeholder="e.g. 49.00 (blank = free)"
                    value={formData.priceYearly} onChange={e => setFormData({ ...formData, priceYearly: e.target.value })} />
                </div>
                <div className="compact-row">
                  <label>Max reflections (blank = unlimited)</label>
                  <input type="number" min="1" placeholder="e.g. 10"
                    value={formData.maxReflections} onChange={e => setFormData({ ...formData, maxReflections: e.target.value })} />
                </div>
                <div className="compact-row">
                  <label>Max family members (blank = N/A)</label>
                  <input type="number" min="1" placeholder="e.g. 3"
                    value={formData.maxFamilyMembers} onChange={e => setFormData({ ...formData, maxFamilyMembers: e.target.value })} />
                </div>
                <div className="compact-row">
                  <label>Extra member $/mo (blank = N/A)</label>
                  <input type="number" step="0.01" min="0" placeholder="e.g. 3.00"
                    value={formData.extraMemberMonthly} onChange={e => setFormData({ ...formData, extraMemberMonthly: e.target.value })} />
                </div>
              </div>

              <div className="compact-toggle-grid" style={{ marginBottom: '0.5rem' }}>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.allowsExport}
                    onChange={e => setFormData({ ...formData, allowsExport: e.target.checked })} />
                  <span>Allows export/download</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.allowsSharingSend}
                    onChange={e => setFormData({ ...formData, allowsSharingSend: e.target.checked })} />
                  <span>Allows sending sharing invites</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.allowsFamily}
                    onChange={e => setFormData({ ...formData, allowsFamily: e.target.checked })} />
                  <span>Family plan (archive + members)</span>
                </label>
              </div>

              <div className="compact-row">
                <label>Feature bullets (one per line)</label>
                <textarea
                  value={formData.featuresText}
                  onChange={e => setFormData({ ...formData, featuresText: e.target.value })}
                  rows={4}
                  placeholder="Unlimited reflections&#10;Full video quality&#10;Export enabled"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-save">Save</button>
                <button type="button" onClick={closeModal} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminPlans;
